import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse, ITableFilters, ITableQuery } from '../../types';
import { parseQueryParams } from '../../utils';
import { SystemModule } from '../../enums/module';
import {
  IApprovalLogTable,
  IBillToCustomer,
  ICreateRequisition,
  IGetRequisitionDetails,
  IMaterialDropdownInput,
  IOrderRequisitionHomeTable,
  IReportConfigTableColumns,
  IRequisitionCustomer,
  IRequisitionReportRow,
  IShipToDropdownInput,
  IUOMDropdownInput,
  RequisitionReportConfig,
  IApprovalConfigPayload,
  IApprovalLogQueryParams,
  IRequisitionUpdateDto,
} from '../../pages/Requisitions/constants/requisition.interface';
import { IProcessChangeLogTable } from '../../types/process';
import { ExportActions } from '../../enums';
import { baseQueryWithReauth } from './baseQuery';

const requisitionApi = createApi({
  reducerPath: 'requisitionApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'Requisitions',
    'RequisitionLogs',
    'RequisitionApprovals',
    'RequisitionActions',
    'ApprovalLogs',
    'ApprovalReports',
    'ApprovalReportsConfig',
    'AllRequisitionReportConfig',
  ],
  endpoints: builder => ({
    getRequisitionMaterial: builder.query<
      BackendResponse<IMaterialDropdownInput[]>,
      ITableFilters | null
    >({
      query: (_?: ITableFilters) => ({
        url: '/requisition/materials',
      }),
    }),
    getRequisitionShipTo: builder.query<
      BackendResponse<IShipToDropdownInput[]>,
      void
    >({
      query: () => ({
        url: '/requisition/ship-to',
      }),
    }),
    getRequisitionBillTo: builder.query<
      BackendResponse<IBillToCustomer[]>,
      void
    >({
      query: () => ({
        url: '/requisition/bill-to',
      }),
    }),
    getRequisitionCustomer: builder.query<
      BackendResponse<IRequisitionCustomer[]>,
      ITableFilters
    >({
      query: (_: ITableFilters) => ({
        url: `/requisition/customers`,
      }),
    }),
    getRequisitionMaterialUom: builder.query<
      BackendResponse<IUOMDropdownInput[]>,
      void
    >({
      query: () => ({
        url: '/requisition/material-uom',
      }),
    }),
    createRequisition: builder.mutation<
      BackendResponse<void>,
      ICreateRequisition
    >({
      query: (payload: ICreateRequisition) => ({
        url: '/requisition',
        method: 'POST',
        body: {
          ...payload,
          module: SystemModule.REQUISITION,
        },
      }),
      invalidatesTags: ['Requisitions', 'RequisitionApprovals'],
    }),
    getRequisition: builder.query<
      BackendResponse<{ orders: IOrderRequisitionHomeTable[]; count?: number }>,
      ITableFilters
    >({
      query: (params: ITableFilters = { page: 1, pageSize: 25 }) => {
        return {
          url: `/requisition${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['Requisitions'];
      },
    }),
    downloadDataInExcel: builder.query<
      Blob,
      { action: ExportActions; query: any }
    >({
      query: (param: any) => {
        const { action, query } = param;
        return {
          url: `/requisition/export/${action}${parseQueryParams(query)}`,
          method: 'GET',
          responseHandler: response => response.blob(),
        };
      },
    }),
    getRequisitionById: builder.query<
      BackendResponse<IGetRequisitionDetails>,
      number
    >({
      query: requisitionId => {
        return {
          url: `/requisition/${requisitionId}`,
          method: 'GET',
        };
      },
      providesTags: (__, _, id) => {
        return [{ type: 'Requisitions', id }];
      },
    }),
    updateRequisitionById: builder.mutation<
      BackendResponse<void>,
      IRequisitionUpdateDto
    >({
      query: ({ id, ...payload }) => ({
        url: `/requisition/${id}`,
        method: 'PUT',
        body: {
          ...payload,
          module: SystemModule.REQUISITION,
        },
      }),
      invalidatesTags: (_, error, { id }) => {
        if (error) {
          return [];
        }
        return [
          { type: 'Requisitions', id },
          { type: 'RequisitionLogs', id },
          'Requisitions',
          'RequisitionApprovals',
        ];
      },
    }),
    deleteRequisitionById: builder.mutation<BackendResponse<void>, number>({
      query: (id: number) => ({
        url: `/requisition/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, error, id) => {
        if (error) {
          return [];
        }
        return [
          { type: 'Requisitions', id },
          { type: 'RequisitionLogs', id },
          'Requisitions',
          'RequisitionApprovals',
        ];
      },
    }),
    getRequisitionLogsById: builder.query<
      BackendResponse<{ list: IProcessChangeLogTable[]; count: number }>,
      { id: number | string; filters: ITableFilters }
    >({
      query: ({ id, filters }) => {
        const query = parseQueryParams(filters);
        return {
          url: `/requisition/logs/${id}${query}`,
          method: 'GET',
        };
      },
      providesTags: (__, _, { id }) => {
        return [{ type: 'RequisitionLogs', id }];
      },
    }),
    getSubmittedRequisitionForApprovals: builder.query<
      BackendResponse<{ list: IOrderRequisitionHomeTable[]; count?: number }>,
      ITableQuery
    >({
      query: (
        params = { module: SystemModule.REQUISITION, page: 1, pageSize: 10 },
      ) => {
        if (params) {
          params = {
            ...params,
            module: SystemModule.REQUISITION,
          };
        }
        return {
          url: `/requisition/approvals${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['RequisitionApprovals'];
      },
    }),
    getApprovalLogs: builder.query<
      BackendResponse<{ list: IApprovalLogTable[]; count?: number }>,
      IApprovalLogQueryParams
    >({
      query: params => {
        return {
          url: `/requisition/approvals/logs${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: (__, _, { id }) => {
        return [{ type: 'ApprovalLogs', id }];
      },
    }),
    getRequisitionReport: builder.query<
      BackendResponse<{ list: IRequisitionReportRow[]; count?: number }>,
      ITableQuery
    >({
      query: params => {
        params = {
          ...params,
          module: SystemModule.REQUISITION,
        };
        return {
          url: `/requisition/reports${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['ApprovalReports'];
      },
    }),
    getRequisitionReportDefaultConfig: builder.query<
      BackendResponse<{ configData: RequisitionReportConfig; id: number }>,
      ITableQuery
    >({
      query: () => {
        const params = {
          module: SystemModule.REQUISITION,
        };
        return {
          url: `/requisition/reports/config${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['ApprovalReportsConfig'];
      },
    }),
    createRequisitionReportConfig: builder.mutation<
      BackendResponse<void>,
      IApprovalConfigPayload
    >({
      query: payload => {
        payload = {
          ...payload,
          module: SystemModule.REQUISITION,
        };
        return {
          url: `/requisition/reports/config`,
          method: 'POST',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ApprovalReportsConfig', 'AllRequisitionReportConfig'];
      },
    }),
    updateRequisitionReportConfig: builder.mutation<
      BackendResponse<void>,
      IApprovalConfigPayload
    >({
      query: payload => {
        payload = {
          ...payload,
          module: SystemModule.REQUISITION,
        };
        return {
          url: `/requisition/reports/config`,
          method: 'PUT',
          body: payload,
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['ApprovalReportsConfig', 'AllRequisitionReportConfig'];
      },
    }),
    getAllRequisitionReportConfigVariants: builder.query<
      BackendResponse<{ list: IReportConfigTableColumns[]; count?: number }>,
      ITableQuery
    >({
      query: (
        params = { module: SystemModule.REQUISITION, page: 1, pageSize: 10 },
      ) => {
        return {
          url: `requisition/reports/config/all${parseQueryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: () => {
        return ['AllRequisitionReportConfig'];
      },
    }),
    deleteRequisitionReportConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => {
        return {
          url: `/requisition/reports/config/${id}`,
          method: 'DELETE',
          params: { module: SystemModule.REQUISITION },
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['AllRequisitionReportConfig', 'ApprovalReportsConfig'];
      },
    }),
    updateRequisitionReportGlobalVariantConfig: builder.mutation<
      BackendResponse<void>,
      number
    >({
      query: id => {
        return {
          url: `/requisition/reports/config/global/${id}`,
          method: 'PUT',
          body: {
            module: SystemModule.REQUISITION,
          },
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['AllRequisitionReportConfig'];
      },
    }),
    updateRequisitionReportDefaultVariantConfig: builder.mutation<
      BackendResponse<void>,
      number
    >({
      query: id => {
        return {
          url: `/requisition/reports/config/default/${id}`,
          method: 'PUT',
          body: {
            module: SystemModule.REQUISITION,
          },
        };
      },
      invalidatesTags: (_, error) => {
        if (error) {
          return [];
        }
        return ['AllRequisitionReportConfig', 'ApprovalReportsConfig'];
      },
    }),
    pushToErpById: builder.mutation<
      BackendResponse<IOrderRequisitionHomeTable[]>,
      number
    >({
      query: id => {
        return {
          url: `/requisition/push/erp`,
          method: 'POST',
          body: {
            module: SystemModule.REQUISITION,
            id,
          },
        };
      },
    }),
  }),
});

export const {
  useGetRequisitionMaterialQuery,
  useGetRequisitionShipToQuery,
  useGetRequisitionBillToQuery,
  useGetRequisitionCustomerQuery,
  useGetRequisitionMaterialUomQuery,
  useCreateRequisitionMutation,
  useGetRequisitionQuery,
  useGetRequisitionByIdQuery,
  useUpdateRequisitionByIdMutation,
  useLazyGetRequisitionQuery,
  useLazyGetRequisitionMaterialQuery,
  useLazyGetRequisitionShipToQuery,
  useLazyGetRequisitionBillToQuery,
  useLazyGetRequisitionCustomerQuery,
  useDeleteRequisitionByIdMutation,
  useGetRequisitionLogsByIdQuery,
  useLazyGetRequisitionLogsByIdQuery,
  useGetSubmittedRequisitionForApprovalsQuery,
  useGetApprovalLogsQuery,
  useGetRequisitionReportDefaultConfigQuery,
  useGetRequisitionReportQuery,
  useCreateRequisitionReportConfigMutation,
  useGetAllRequisitionReportConfigVariantsQuery,
  useDeleteRequisitionReportConfigMutation,
  useUpdateRequisitionReportGlobalVariantConfigMutation,
  useUpdateRequisitionReportConfigMutation,
  useUpdateRequisitionReportDefaultVariantConfigMutation,
  usePushToErpByIdMutation,
  useLazyDownloadDataInExcelQuery,
} = requisitionApi;

export default requisitionApi;
